import {
  compareISODate,
  getPairPreset,
  getSeasonalityWindow,
  parseISODate,
  toSeasonalDayKey,
  type ActualContractPair,
  type EnabledCanolaMonthCode,
  type ISODate,
  type PairPresetId,
} from "../domain";
import { mean, median, medianAbsoluteDeviation, quantile, sampleStandardDeviation } from "./math";

export interface SeasonalValuePoint {
  readonly date: ISODate;
  readonly value: number;
}

export interface SeasonalValueEpisode {
  readonly pairPresetId: PairPresetId;
  /** Month-code identity such as H/K. It deliberately excludes contract years. */
  readonly templateId: string;
  readonly contractYear: number;
  /** The real, unadjusted contracts whose settlements produced this episode. */
  readonly actualContracts: ActualContractPair;
  readonly points: readonly SeasonalValuePoint[];
}

export type SeasonalLocation = "mean" | "median";
export type SeasonalDispersion = "standard-deviation" | "winsorized-standard-deviation" | "mad";

export interface SeasonalBaselineOptions {
  readonly minimumHistoricalYears?: number;
  readonly halfWindowBusinessObservations?: number;
  readonly location?: SeasonalLocation;
  readonly dispersion?: SeasonalDispersion;
}

export interface AvailableSeasonalBaselinePoint {
  readonly status: "available";
  readonly date: ISODate;
  readonly seasonalKey: string;
  readonly location: number;
  readonly dispersion: number;
  readonly sampleSize: number;
  readonly historicalYears: readonly number[];
  readonly method: string;
}

export interface UnavailableSeasonalBaselinePoint {
  readonly status: "not-available";
  readonly date: ISODate;
  readonly seasonalKey: string | null;
  readonly reason: "leap-day-excluded" | "insufficient-historical-years" | "zero-dispersion";
  readonly historicalYears: readonly number[];
  readonly sampleSize: number;
}

export type SeasonalBaselinePoint =
  | AvailableSeasonalBaselinePoint
  | UnavailableSeasonalBaselinePoint;

function monthDayOrdinal(key: string): number {
  const [monthText, dayText] = key.split("-");
  const month = Number(monthText);
  const day = Number(dayText);
  const current = Date.UTC(2001, month - 1, day);
  const start = Date.UTC(2001, 0, 1);
  return Math.round((current - start) / 86_400_000);
}

function seasonalDistance(left: string, right: string): number {
  const distance = Math.abs(monthDayOrdinal(left) - monthDayOrdinal(right));
  return Math.min(distance, 365 - distance);
}

const ENABLED_CANOLA_MONTH_CODES = new Set<string>(["F", "H", "K", "N", "X"]);
const MAXIMUM_SEASONAL_DISTANCE_DAYS = 10;

function validateEpisode(episode: SeasonalValueEpisode): void {
  if (!Number.isInteger(episode.contractYear)) {
    throw new Error("Seasonal episode contractYear must be an integer.");
  }
  if (episode.actualContracts.pairId !== episode.pairPresetId) {
    throw new Error("Seasonal episode pairPresetId does not match its actual contracts.");
  }
  const preset = getPairPreset(episode.pairPresetId);
  if (
    episode.actualContracts.legA.instrumentId !== preset.legA ||
    episode.actualContracts.legB.instrumentId !== preset.legB
  ) {
    throw new Error("Seasonal episode actual contracts do not match the pair preset instruments.");
  }
  const expectedTemplate = `${episode.actualContracts.legA.monthCode}/${episode.actualContracts.legB.monthCode}`;
  if (episode.templateId !== expectedTemplate) {
    throw new Error(`Seasonal episode templateId must identify its actual contracts (${expectedTemplate}).`);
  }
  const canola = preset.canolaLeg === "legA"
    ? episode.actualContracts.legA
    : episode.actualContracts.legB;
  if (
    !ENABLED_CANOLA_MONTH_CODES.has(canola.monthCode) ||
    canola.monthCode === "U"
  ) {
    throw new Error("Seasonal episode uses a disabled Canola contract month.");
  }
  if (
    episode.actualContracts.legA.year !== episode.contractYear ||
    episode.actualContracts.legB.year !== episode.contractYear
  ) {
    throw new Error("Seasonal episode contractYear must match both actual contract years.");
  }
  if (new Set(episode.points.map((point) => point.date)).size !== episode.points.length) {
    throw new Error(`Seasonal episode ${episode.contractYear} contains duplicate dates.`);
  }
  const window = getSeasonalityWindow(
    canola.monthCode as EnabledCanolaMonthCode,
    episode.contractYear,
  );
  episode.points.forEach((point, index) => {
    parseISODate(point.date);
    if (!Number.isFinite(point.value)) {
      throw new Error(`Seasonal episode contains a non-finite value (${point.date}).`);
    }
    const previous = episode.points[index - 1];
    if (previous && compareISODate(previous.date, point.date) >= 0) {
      throw new Error(`Seasonal episode ${episode.contractYear} points must be strictly date-ordered.`);
    }
    if (compareISODate(point.date, window.start) < 0 || compareISODate(point.date, window.end) > 0) {
      throw new Error(`Seasonal point ${point.date} is outside its actual contract episode window.`);
    }
  });
}

function validatePanel(current: SeasonalValueEpisode, episodes: readonly SeasonalValueEpisode[]): void {
  validateEpisode(current);
  const years = new Set<number>([current.contractYear]);
  for (const episode of episodes) {
    validateEpisode(episode);
    if (years.has(episode.contractYear)) {
      throw new Error(`Duplicate seasonal contract year: ${episode.contractYear}.`);
    }
    years.add(episode.contractYear);
    if (
      episode.pairPresetId !== current.pairPresetId ||
      episode.templateId !== current.templateId ||
      episode.actualContracts.legA.instrumentId !== current.actualContracts.legA.instrumentId ||
      episode.actualContracts.legB.instrumentId !== current.actualContracts.legB.instrumentId ||
      episode.actualContracts.legA.monthCode !== current.actualContracts.legA.monthCode ||
      episode.actualContracts.legB.monthCode !== current.actualContracts.legB.monthCode
    ) {
      throw new Error("Seasonal baseline cannot mix pair presets or contract templates.");
    }
  }
}

function smoothedValuesForEpisode(
  targetKey: string,
  episode: SeasonalValueEpisode,
  halfWindow: number,
): readonly number[] {
  const eligible = episode.points
    .filter((point) => {
      const key = toSeasonalDayKey(point.date);
      return key !== null && seasonalDistance(targetKey, key) <= MAXIMUM_SEASONAL_DISTANCE_DAYS;
    });
  if (eligible.length === 0) return [];
  let closestIndex = 0;
  let closestDistance = Number.POSITIVE_INFINITY;
  eligible.forEach((point, index) => {
    const key = toSeasonalDayKey(point.date);
    const distance = key === null ? Number.POSITIVE_INFINITY : seasonalDistance(targetKey, key);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestIndex = index;
    }
  });
  return eligible
    .slice(Math.max(0, closestIndex - halfWindow), closestIndex + halfWindow + 1)
    .map((point) => point.value);
}

function dispersion(values: readonly number[], method: SeasonalDispersion): number {
  if (method === "mad") return medianAbsoluteDeviation(values) * 1.4826;
  if (method === "winsorized-standard-deviation") {
    const lower = quantile(values, 0.05);
    const upper = quantile(values, 0.95);
    return sampleStandardDeviation(values.map((value) => Math.min(upper, Math.max(lower, value))));
  }
  return sampleStandardDeviation(values);
}

/**
 * Estimates the current episode's seasonal expectation using only strictly
 * earlier contract years. The ±window is counted in each historical episode's
 * ordered eligible observations, which approximates business-day smoothing
 * without synthesizing exchange holidays.
 */
export function estimateSeasonalBaseline(
  current: SeasonalValueEpisode,
  episodes: readonly SeasonalValueEpisode[],
  options: SeasonalBaselineOptions = {},
): readonly SeasonalBaselinePoint[] {
  const minimumHistoricalYears = options.minimumHistoricalYears ?? 5;
  const halfWindow = options.halfWindowBusinessObservations ?? 10;
  const locationMethod = options.location ?? "mean";
  const dispersionMethod = options.dispersion ?? "standard-deviation";
  if (minimumHistoricalYears < 1 || halfWindow < 0 || !Number.isInteger(halfWindow)) {
    throw new Error("Invalid seasonal baseline options.");
  }
  validatePanel(current, episodes);

  const historical = episodes
    .filter((episode) => episode.contractYear < current.contractYear)
    .sort((left, right) => left.contractYear - right.contractYear);

  return current.points.map((point) => {
    const targetKey = toSeasonalDayKey(point.date);
    if (targetKey === null) {
      return {
        status: "not-available",
        date: point.date,
        seasonalKey: null,
        reason: "leap-day-excluded",
        historicalYears: [],
        sampleSize: 0,
      };
    }
    const contributions = historical.map((episode) => ({
      year: episode.contractYear,
      values: smoothedValuesForEpisode(targetKey, episode, halfWindow),
    })).filter((contribution) => contribution.values.length > 0);
    const historicalYears = contributions.map((contribution) => contribution.year);
    const values = contributions.flatMap((contribution) => contribution.values);
    if (historicalYears.length < minimumHistoricalYears) {
      return {
        status: "not-available",
        date: point.date,
        seasonalKey: targetKey,
        reason: "insufficient-historical-years",
        historicalYears,
        sampleSize: values.length,
      };
    }
    const location = locationMethod === "median" ? median(values) : mean(values);
    const scale = dispersion(values, dispersionMethod);
    if (!Number.isFinite(scale) || scale <= 0) {
      return {
        status: "not-available",
        date: point.date,
        seasonalKey: targetKey,
        reason: "zero-dispersion",
        historicalYears,
        sampleSize: values.length,
      };
    }
    return {
      status: "available",
      date: point.date,
      seasonalKey: targetKey,
      location,
      dispersion: scale,
      sampleSize: values.length,
      historicalYears,
      method: `${locationMethod}; ${dispersionMethod}; ±${halfWindow} eligible business observations capped at ±${MAXIMUM_SEASONAL_DISTANCE_DAYS} seasonal days; Feb 29 excluded`,
    };
  });
}

export interface SeasonalZPoint {
  readonly date: ISODate;
  readonly status: "available" | "not-available";
  readonly value: number | null;
  readonly reason?: UnavailableSeasonalBaselinePoint["reason"];
  readonly direction: "positive-is-leg-a-rich";
  readonly baselineYears: readonly number[];
}

export function calculateSeasonalZScores(
  current: SeasonalValueEpisode,
  episodes: readonly SeasonalValueEpisode[],
  options: SeasonalBaselineOptions = {},
): readonly SeasonalZPoint[] {
  const baseline = estimateSeasonalBaseline(current, episodes, options);
  return current.points.map((point, index) => {
    const expectation = baseline[index];
    if (!expectation || expectation.status === "not-available") {
      return {
        date: point.date,
        status: "not-available",
        value: null,
        ...(expectation ? { reason: expectation.reason } : {}),
        direction: "positive-is-leg-a-rich",
        baselineYears: expectation?.historicalYears ?? [],
      };
    }
    return {
      date: point.date,
      status: "available",
      value: (point.value - expectation.location) / expectation.dispersion,
      direction: "positive-is-leg-a-rich",
      baselineYears: expectation.historicalYears,
    };
  });
}

export interface SeasonalPanelAnalysis {
  readonly scope: "seasonal-panel";
  readonly currentContractYear: number;
  readonly leapDayPolicy: "exclude-feb-29";
  readonly baseline: readonly SeasonalBaselinePoint[];
  readonly seasonalZ: readonly SeasonalZPoint[];
  readonly excludedContractYears: readonly number[];
}

export function analyzeSeasonalPanel(
  current: SeasonalValueEpisode,
  episodes: readonly SeasonalValueEpisode[],
  options: SeasonalBaselineOptions = {},
): SeasonalPanelAnalysis {
  const baseline = estimateSeasonalBaseline(current, episodes, options);
  const seasonalZ = calculateSeasonalZScores(current, episodes, options);
  return {
    scope: "seasonal-panel",
    currentContractYear: current.contractYear,
    leapDayPolicy: "exclude-feb-29",
    baseline,
    seasonalZ,
    excludedContractYears: episodes
      .filter((episode) => episode.contractYear > current.contractYear)
      .map((episode) => episode.contractYear),
  };
}

/** Useful for callers validating a dated panel point before alignment. */
export function isDateInsideContractYear(point: SeasonalValuePoint, contractYear: number): boolean {
  return parseISODate(point.date).year <= contractYear;
}
